import logger from "../utils/logger";
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import * as propertiesService from '../services/supabase/properties';

/**
 * Custom hook for managing properties with Supabase
 * Automatically fetches properties for the authenticated user
 */
export function useProperties() {
  const { user } = useAuth();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMountedRef = useRef(true);

  // Fetch properties on mount and when user ID changes
  // Wait for profile to be loaded before fetching (prevents race condition on page refresh)
  const fetchProperties = useCallback(async () => {
    logger.log('[useProperties] fetchProperties called:', {
      hasUser: !!user,
      userId: user?.id,
    });

    if (!user) {
      logger.log('[useProperties] No user, returning empty array');
      setProperties([]);
      setLoading(false);
      return;
    }

    // Profile not required - user is enough for RLS
    // Removed profile check to prevent infinite waiting

    logger.log('[useProperties] Starting fetch...');
    const fetchStartTime = Date.now();
    let timeoutId;

    try {
      setLoading(true);
      setError(null);

      // Force timeout after 10s to prevent infinite hanging
      const timeoutPromise = new Promise((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(new Error('Query timeout - sesja może być nieprawidłowa'));
        }, 10000);
      });

      const dataPromise = propertiesService.getProperties();
      const data = await Promise.race([dataPromise, timeoutPromise]);

      if (timeoutId) clearTimeout(timeoutId);

      if (!isMountedRef.current) {
        return; // Component unmounted, don't update state
      }

      logger.log(`[useProperties] Fetch completed in ${Date.now() - fetchStartTime}ms`);
      setProperties(data);
    } catch (err) {
      if (timeoutId) clearTimeout(timeoutId);

      if (isMountedRef.current) {
        console.error('[useProperties] Error fetching properties:', err);
        setError(err.message);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    isMountedRef.current = true;
    fetchProperties();

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchProperties]);

  const createProperty = async (propertyData) => {
    try {
      const newProperty = await propertiesService.createProperty(propertyData);
      setProperties(prev => [newProperty, ...prev]);
      return { success: true, data: newProperty };
    } catch (err) {
      console.error('Error creating property:', err);
      return { success: false, error: err.message };
    }
  };

  const updateProperty = async (id, updates) => {
    try {
      const updatedProperty = await propertiesService.updateProperty(id, updates);
      setProperties(prev =>
        prev.map(p => (p.id === id ? updatedProperty : p))
      );
      return { success: true, data: updatedProperty };
    } catch (err) {
      console.error('Error updating property:', err);
      return { success: false, error: err.message };
    }
  };

  const deleteProperty = async (id) => {
    try {
      await propertiesService.deleteProperty(id);
      setProperties(prev => prev.filter(p => p.id !== id));
      return { success: true };
    } catch (err) {
      console.error('Error deleting property:', err);
      return { success: false, error: err.message };
    }
  };

  const refresh = () => {
    fetchProperties();
  };

  return {
    properties,
    loading,
    error,
    createProperty,
    updateProperty,
    deleteProperty,
    refresh,
  };
}
